<template>
  <div>
    <trac-loading v-if="isLoading" />
    <div v-else>
      <div class="grid grid-cols-12 gap-3 mb-8">
        <div
          class="bg-white rounded-xl border border-primaryBlue col-span-3 p-4"
        >
          <h3 class="text-sm text-primaryBlue mb-2">Total Products</h3>
          <p class="text-primaryBlue text-3xl font-bold">
            {{ productsData.total_count }}
          </p>
        </div>
        <div
          class="bg-white rounded-xl border border-primaryBlue col-span-3 p-4"
        >
          <h3 class="text-sm text-primaryBlue mb-2">Stock Managed</h3>
          <p class="text-primaryBlue text-3xl font-bold">
            {{ productsData.stock_manage_count }}
          </p>
        </div>
      </div>

      <table class="w-full table-auto">
        <thead class="bg-blue-100">
          <tr>
            <th class="text-left whitespace-no-wrap p-4 text-xs font-semibold">
              Name
            </th>
            <th class="text-left whitespace-no-wrap p-4 text-xs font-semibold">
              Current Stock
            </th>
            <th class="text-left whitespace-no-wrap p-4 text-xs font-semibold">
              Store(s)
            </th>
            <th class="text-left whitespace-no-wrap p-4 text-xs font-semibold">
              Last Updated
            </th>
            <th class="text-left whitespace-no-wrap p-4 text-xs font-semibold">
              Actions
            </th>
          </tr>
        </thead>
        <tbody v-if="productsData.products && productsData.products.length">
          <tr
            v-for="(product, index) in productsData.products"
            :key="index"
            :class="index % 2 === 0 ? '' : 'bg-gray-100'"
            class="cursor-pointer hover:bg-gray-200"
            @click="showProductDetails(product)"
          >
            <td class="text-xs p-4 font-bold capitalize">
              {{ product.title }}
            </td>
            <td class="text-xs p-4 font-bold capitalize">
              {{ product.stockQuantity }}
            </td>
            <td class="text-xs p-4 font-bold capitalize">
              <span>
                {{ product.storeStock ? product.storeStock.length : 0 }}
                store{{
                  product.storeStock && product.storeStock.length > 1 ? 's' : ''
                }}
              </span>
            </td>
            <td class="text-xs p-4 font-bold capitalize">
              {{ moment(product.updatedAt).format('Do MMM, YYYY hh:mma') }}
            </td>
            <td class="text-xs p-4 font-bold capitalize">
              <div v-if="product.manageStock" class="flex gap-2" @click.stop>
                <trac-button
                  class="h-8 flex items-center justify-center"
                  variant="small"
                  @button-clicked="selectAdjustmentType('add', product)"
                >
                  Add Stock
                </trac-button>
                <trac-button
                  class="h-8 flex items-center justify-center"
                  :disabled="product.stockQuantity <= 0"
                  variant="danger-outline"
                  @button-clicked="selectAdjustmentType('remove', product)"
                >
                  Remove Stock
                </trac-button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="5">
              <trac-center-data>
                <div class="h-40 flex items-center text-lg text-gray-600">
                  {{
                    params.keyword.length > 0 &&
                    productsData.products.length > 0
                      ? 'No match found.'
                      : 'No product added yet.'
                  }}
                </div>
              </trac-center-data>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!isLoading" class="flex justify-center mt-4">
        <trac-button
          :disabled="
            (productsData.products && productsData.products.length) >=
            productsData.total_count
          "
          @button-clicked="loadMoreProducts"
        >
          {{
            (productsData.products && productsData.products.length) >=
            productsData.total_count
              ? 'No More Items'
              : 'Load More'
          }}
        </trac-button>
      </div>

      <StockDetails
        v-if="showDetailsModal"
        :product="selectedProduct"
        @close="showDetailsModal = false"
      />
      <trac-modal
        v-if="showSelectStoreModal"
        @close="showSelectStoreModal = false"
      >
        <SelectStore
          :adjustmentType="adjustmentType"
          :stores="stores"
        ></SelectStore>
      </trac-modal>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { eventBus } from '../../../eventBus';
import moment from 'moment';
import SelectStore from '../../../views/inventory-control/stock-adjustment/SelectStore.vue';
import StockDetails from './StockDetails';
import { SAVE_LOCAL_DB_DATA } from '../../../browser-db-config/localStorage';

export default {
  name: 'StockDashboard',
  components: { SelectStore, StockDetails },
  data() {
    return {
      moment,
      isLoading: false,
      params: {
        productType: 'simple',
        page: 1,
        limit: 20,
        keyword: '',
      },
      showSelectStoreModal: false,
      showDetailsModal: false,
      productsData: {},
      stores: [],
      adjustmentType: '',
      selectedProduct: null,
    };
  },
  created() {
    this.fetchProducts();
    this.fetchAllStores();
  },
  methods: {
    ...mapActions(['FETCH_PRODUCTS_V2', 'FETCH_ALL_STORES']),
    async fetchProducts() {
      this.isLoading = true;
      try {
        const res = await this.FETCH_PRODUCTS_V2({
          params: this.params,
          includeVariants: true,
          queryText: 'variant',
        });
        this.productsData = res.data;
      } catch (error) {
        eventBus.$emit('trac-alert', {
          message: error?.response?.data?.message,
        });
      } finally {
        this.isLoading = false;
      }
    },
    loadMoreProducts() {
      this.params = {
        ...this.params,
        page: 1,
        limit: this.params.limit + 10,
      };

      this.fetchProducts();
    },
    async fetchAllStores() {
      await this.FETCH_ALL_STORES();
      const res = this.$store.getters['GET_ALL_STORES'];

      if (res.status) {
        this.stores = res.data || [];
      }
    },
    selectAdjustmentType(val, product) {
      this.adjustmentType = val;
      this.showSelectStoreModal = true;
      SAVE_LOCAL_DB_DATA('stock-adjustment-item-selected', product);
    },
    showProductDetails(product) {
      this.showDetailsModal = true;
      this.selectedProduct = product;
    },
  },
};
</script>

<style scoped></style>
